import axios from 'axios';
import { getDimensionSet, getIndicatorSeries, getSprinkleQueryColumnSet, getQueryColumnSet } from '../../apiFactory/DashboardDesigner/indicators';
import { getIndicatorData } from '../../apiFactory/DashboardDesigner/SIP-CRUD';

/**
 * DrillByService - Handles fetching dimensions and data for the Drill By feature
 */

// ─── Time-period label parser ──────────────────────────────────────────────
// Converts common time-period label formats into a sortable Unix timestamp (ms).
const parseTimeLabel = (name) => {
	const s = String(name || '').trim();
	// ISO pipe-range: "2024-01-01T00:00:00.000Z|2024-01-31T23:59:59.999Z" — the raw value
	// timerange dimensions carry before display-formatting turns them into "MMM'YY" labels.
	// Without this branch every such value falls through to `return 0`, making time-asc/desc
	// a no-op (every pair compares equal).
	const isoRange = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)\|(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)$/);
	if (isoRange) return new Date(isoRange[1]).getTime();
	// Quarter: "Q1 2024", "Q1-2024", "Q2/2023", "Q3'23" etc.
	const quarter = s.match(/Q(\d)\s*[-/'\s]\s*(\d{2,4})/i);
	if (quarter) {
		const q = parseInt(quarter[1], 10);
		const rawYear = quarter[2];
		const y = rawYear.length <= 2 ? 2000 + parseInt(rawYear, 10) : parseInt(rawYear, 10);
		return new Date(y, (q - 1) * 3, 1).getTime();
	}
	// Fiscal year: "FY2024", "FY24", "FY 2024"
	const fy = s.match(/^FY\s*(\d{2,4})$/i);
	if (fy) {
		const rawYear = fy[1];
		const y = rawYear.length <= 2 ? 2000 + parseInt(rawYear, 10) : parseInt(rawYear, 10);
		return new Date(y, 0, 1).getTime();
	}
	// ISO / year-month: "2024-01", "2024-01-15", "2024/01"
	const iso = s.match(/^(\d{4})[-/](\d{2})(?:[-/](\d{2}))?$/);
	if (iso) return new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3] || '1', 10)).getTime();
	// Year only: "2024"
	if (/^\d{4}$/.test(s)) return new Date(parseInt(s, 10), 0, 1).getTime();
	// Month+Year with apostrophe/space/hyphen: "Jan'24", "Jan 2024", "jan-2024"
	const monthYear = s.match(/^([A-Za-z]+)['.\s-]+(\d{2,4})$/);
	if (monthYear) {
		const months = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
		const m = months[monthYear[1].toLowerCase().slice(0, 3)];
		if (m !== undefined) {
			const rawYear = monthYear[2];
			const y = rawYear.length <= 2 ? 2000 + parseInt(rawYear, 10) : parseInt(rawYear, 10);
			return new Date(y, m, 1).getTime();
		}
	}
	// month/year reversed: "01/2024"
	const mdy = s.match(/^(\d{2})\/(\d{4})$/);
	if (mdy) return new Date(parseInt(mdy[2], 10), parseInt(mdy[1], 10) - 1, 1).getTime();
	return 0;
};

/**
 * Applies a sort order to an array of drill-by data items.
 * Exported so modal toolbars can re-sort client-side without a new API call.
 *
 * @param {Array}  data      - Array of { name, value, ... } items
 * @param {string} sortOrder - 'value-desc' | 'value-asc' | 'label-asc' | 'label-desc' | 'time-asc' | 'time-desc'
 * @returns {Array} - New sorted array (original is not mutated)
 */
// Sums all series1..N keys as the sort metric when a row has no flat "value" (multi-measure rows).
const sortMetric = item => {
	if (typeof item.value === 'number') return item.value;
	return Object.keys(item)
		.filter(k => /^series\d+$/.test(k))
		.reduce((sum, k) => sum + (item[k] || 0), 0);
};

export const applySortOrder = (data, sortOrder) => {
	if (!Array.isArray(data) || data.length === 0) return data;
	const sorted = [...data];
	switch (sortOrder) {
		case 'value-asc':
			return sorted.sort((a, b) => sortMetric(a) - sortMetric(b));
		case 'label-asc':
			return sorted.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
		case 'label-desc':
			return sorted.sort((a, b) => String(b.name || '').localeCompare(String(a.name || '')));
		case 'time-asc':
			return sorted.sort((a, b) => parseTimeLabel(a.name) - parseTimeLabel(b.name));
		case 'time-desc':
			return sorted.sort((a, b) => parseTimeLabel(b.name) - parseTimeLabel(a.name));
		case 'value-desc':
		default:
			return sorted.sort((a, b) => sortMetric(b) - sortMetric(a));
	}
};

/**
 * Fetches available dimensions for drill-by based on indicator IDs or query ID
 * 
 * @param {Array} dataConfig - The widget's data configuration
 * @param {boolean} isExternal - Whether this is an external (Sprinkle) query
 * @returns {Promise<Array>} - Array of dimension names
 */
export const fetchDrillByDimensions = async (dataConfig, isExternal = false) => {
	if (!dataConfig || !Array.isArray(dataConfig) || dataConfig.length === 0) {
		return [];
	}
	
	const firstConfig = dataConfig[0];
	
	// Check if it's a query-based widget
	if (firstConfig.queryId) {
		try {
			if (isExternal) {
				const dimensionSet = await getSprinkleQueryColumnSet(firstConfig.queryId);
				return dimensionSet.map(elem => elem.name);
			} else {
				const result = await getQueryColumnSet(firstConfig.queryId);
				return Array.isArray(result.keys) ? result.keys : [];
			}
		} catch (error) {
			console.error('Error fetching query dimensions:', error);
			return [];
		}
	}
	
	// Otherwise, it's an indicator-based widget
	const indicatorIds = dataConfig
		.filter(config => config.indicatorId)
		.map(config => config.indicatorId);
	
	if (indicatorIds.length === 0) {
		return [];
	}
	
	try {
		const { dimensionSet } = await getDimensionSet(indicatorIds);
		return dimensionSet || [];
	} catch (error) {
		console.error('Error fetching indicator dimensions:', error);
		return [];
	}
};

// Dimensions where Fusion sends display label (e.g. "Jul'24 - Sep'24") but API expects raw value (e.g. "2024-07-01T...|2024-09-30T...")
// Resolve raw value from chartData when provided (Fusion charts only)
const RESOLVE_RAW_FROM_CHART_DATA_DIMENSIONS = ['fy_timerange', 'timerange_timerange', 'timerange'];

const normalizeLabel = (str) => {
	if (str == null) return '';
	// Decode HTML entities (e.g. &#039; -> ')
	const decoded = String(str)
		.replace(/&#039;/g, "'")
		.replace(/&#39;/g, "'")
		.replace(/&apos;/g, "'")
		.trim()
		.toLowerCase();
	return decoded;
};

/**
 * Resolve the raw dimension value from chart data when Fusion sends only display label.
 * Chart data items have raw value in item[xSeriesDimension]; display may be in label/name.
 * chartData can be: array of items, or { data }, or { categories: [ { label, ... } ] }.
 */
const resolveRawValueFromChartData = (clickedValue, xSeriesDimension, chartData) => {
	if (!clickedValue || !xSeriesDimension || !chartData) return null;
	const normalizedClicked = normalizeLabel(clickedValue);

	let list = [];
	if (Array.isArray(chartData)) {
		list = chartData;
	} else if (chartData?.data && Array.isArray(chartData.data)) {
		list = chartData.data;
	} else if (chartData?.categories && Array.isArray(chartData.categories)) {
		list = chartData.categories;
	} else {
		return null;
	}

	for (const item of list) {
		if (item == null || typeof item !== 'object') continue;
		const raw = item[xSeriesDimension];
		if (raw === undefined || raw === null) continue;
		// Match by display label (Fusion uses label/name for category)
		const itemLabel = item.label ?? item.name ?? item.categoryLabel ?? item.category ?? '';
		if (normalizeLabel(itemLabel) === normalizedClicked) return raw;
		// Also match if raw string equals clicked (Recharts may send raw)
		if (normalizeLabel(String(raw)) === normalizedClicked) return raw;
	}
	return null;
};

/**
 * Fetches drill-by data with a specific dimension
 * For Fusion + fy_timerange, clickedData has display label (e.g. "Jul'24 - Sep'24"); pass chartData
 * to resolve raw value (e.g. "2024-07-01T...|2024-09-30T...") and send that in the filter.
 *
 * @param {Object} params
 * @param {Array} params.dataConfig - The widget's data configuration
 * @param {string} params.drillByDimension - The dimension to drill by
 * @param {string} params.xSeriesDimension - The current x-axis dimension
 * @param {Object} params.clickedData - The data point that was clicked (may have display label from Fusion)
 * @param {Array} [params.chartData] - Chart data array (for Fusion); used to resolve raw value for fy_timerange
 * @param {boolean} params.isExternal - Whether this is an external query
 * @param {number} params.organizationId - Organization ID
 * @returns {Promise<Array>} - Array of data points for the drill-by view
 */
export const fetchDrillByData = async ({
	dataConfig,
	drillByDimension,
	xSeriesDimension,
	clickedData,
	chartData = null,
	isExternal = false,
	organizationId,
	sortOrder = 'value-desc'
}) => {
	if (!dataConfig || !Array.isArray(dataConfig) || dataConfig.length === 0) {
		return [];
	}

	const isChartLevel = !clickedData;

	// Resolve the filter value once - shared by every configured measure below.
	const clickedDisplayValue = clickedData?.name || clickedData?.label || clickedData?.[xSeriesDimension];
	const needsRawValue = xSeriesDimension && RESOLVE_RAW_FROM_CHART_DATA_DIMENSIONS.some(
		d => String(xSeriesDimension).toLowerCase().includes(d)
	);
	const rawFromPayload = clickedData?.[xSeriesDimension];
	const rawValue = needsRawValue
		? (rawFromPayload != null ? rawFromPayload : (chartData ? resolveRawValueFromChartData(clickedDisplayValue, xSeriesDimension, chartData) : null))
		: null;
	const filterValue = rawValue != null ? rawValue : clickedDisplayValue;

	// Fetch rows for a single configured measure - looping over every dataConfig entry (not
	// just the first) is what lets multi-measure stacked/grouped/composed widgets keep all
	// their series in the drill-by view instead of only ever showing the first measure.
	const fetchMeasureRows = async (measureConfig) => {
		const filters = { ...(measureConfig.filters || {}) };
		let newSeriesOn;
		if (isChartLevel) {
			// Chart-level: use only the drill-by dimension so the backend aggregates correctly
			// (stacking on existing dimensions would cause sum across all parent dimension values)
			newSeriesOn = [drillByDimension];
		} else {
			// Plot-level: stack the drill-by dimension on top of current dimensions, then filter to clicked plot
			const currentSeriesOn = measureConfig.seriesOn || measureConfig.actualDimensions || [];
			newSeriesOn = [...new Set([...currentSeriesOn, drillByDimension])];
			if (xSeriesDimension && filterValue) {
				filters[xSeriesDimension] = Array.isArray(filterValue) ? filterValue : [filterValue];
			}
		}

		try {
			if (measureConfig.indicatorId) {
				const indicatorConfig = { indicatorId: measureConfig.indicatorId, seriesOn: newSeriesOn, filters };
				const response = await axios.post('/indicator/getSeries', indicatorConfig);
				return response.data?.response || [];
			}
			if (measureConfig.queryId) {
				const queryConfig = {
					...(isExternal ? { thirdParty: { exploreId: measureConfig.queryId } } : { queryId: measureConfig.queryId }),
					seriesOn: newSeriesOn,
					filters
				};
				const response = await axios.post('/api/facts/filter', queryConfig);
				return response.data?.response || [];
			}
			return [];
		} catch (error) {
			console.error('Error fetching drill-by data for measure:', error);
			return [];
		}
	};

	try {
		const perMeasureRows = await Promise.all(dataConfig.map(fetchMeasureRows));
		return aggregateByDimension(perMeasureRows, drillByDimension, sortOrder);
	} catch (error) {
		console.error('Error fetching drill-by data:', error);
		return [];
	}
};

/**
 * Aggregates data by a specific dimension and applies a sort order.
 *
 * @param {Array}  perMeasureRows - Array of row-arrays, one per configured measure (fetchDrillByData
 *                                  always passes one array per dataConfig entry); a single flat
 *                                  array is also accepted for callers with just one measure.
 * @param {string} dimension      - Dimension to group by
 * @param {string} sortOrder      - Sort order key (default: 'value-desc')
 * @returns {Array} - Aggregated and sorted data: { name, value, ... } for a single measure,
 *                    { name, series1, series2, ..., ... } for multiple measures (same
 *                    "seriesN" naming contract as WidgetViewRenderer's buildDrilldownData).
 */
const aggregateByDimension = (perMeasureRows, dimension, sortOrder = 'value-desc') => {
	const measures = Array.isArray(perMeasureRows) && Array.isArray(perMeasureRows[0])
		? perMeasureRows
		: [Array.isArray(perMeasureRows) ? perMeasureRows : []];

	if (measures.every(rows => !Array.isArray(rows) || rows.length === 0)) {
		return [];
	}

	const extractValue = item => {
		if (typeof item.value === 'number') return item.value;
		const numericFields = Object.keys(item).filter(k => typeof item[k] === 'number' && k !== dimension);
		return numericFields.length > 0 ? item[numericFields[0]] : 0;
	};

	const groups = new Map();
	measures.forEach((rows, measureIndex) => {
		if (!Array.isArray(rows)) return;
		rows.forEach(item => {
			const key = item[dimension];
			if (key === undefined || key === null) return;
			const keyStr = String(key);
			if (!groups.has(keyStr)) {
				groups.set(keyStr, { name: keyStr, [dimension]: key, seriesSums: {} });
			}
			const group = groups.get(keyStr);
			group.seriesSums[measureIndex] = (group.seriesSums[measureIndex] || 0) + extractValue(item);
		});
	});

	const isMultiMeasure = measures.length > 1;
	const result = Array.from(groups.values()).map(({ seriesSums, ...rest }) => {
		if (!isMultiMeasure) {
			return { ...rest, value: seriesSums[0] || 0 };
		}
		const point = { ...rest };
		measures.forEach((_, i) => { point[`series${i + 1}`] = seriesSums[i] || 0; });
		return point;
	});

	return applySortOrder(result, sortOrder);
};

/**
 * Fetches drill-to-detail data - returns ALL columns of raw data for the clicked data point
 * Similar to Superset's "Drill to Detail" feature that shows atomic/granular data
 *
 * @param {Object} params
 * @param {Array} params.dataConfig - The widget's data configuration
 * @param {string} params.xSeriesDimension - The current x-axis dimension
 * @param {Object} params.clickedData - The data point that was clicked
 * @param {Array} [params.chartData] - Chart data array (for Fusion); used to resolve raw value
 * @param {boolean} params.isExternal - Whether this is an external query
 * @param {number} params.organizationId - Organization ID
 * @param {number} params.limit - Maximum number of rows to fetch (default 1000)
 * @param {number} params.offset - Offset for pagination (default 0)
 * @returns {Promise<Object>} - Object containing { data: Array, columns: Array, totalCount: number }
 */
export const fetchDrillToDetailData = async ({
	dataConfig,
	xSeriesDimension,
	clickedData,
	chartData = null,
	isExternal = false,
	organizationId,
	limit = 1000,
	offset = 0
}) => {
	if (!dataConfig || !Array.isArray(dataConfig) || dataConfig.length === 0) {
		return { data: [], columns: [], totalCount: 0 };
	}

	// Resolve the filter value once - shared by every configured measure below.
	const clickedDisplayValue = clickedData?.name || clickedData?.label || clickedData?.[xSeriesDimension];
	const needsRawValue = xSeriesDimension && RESOLVE_RAW_FROM_CHART_DATA_DIMENSIONS.some(
		d => String(xSeriesDimension).toLowerCase().includes(d)
	);
	const rawFromPayload = clickedData?.[xSeriesDimension];
	const rawValue = needsRawValue
		? (rawFromPayload != null ? rawFromPayload : (chartData ? resolveRawValueFromChartData(clickedDisplayValue, xSeriesDimension, chartData) : null))
		: null;
	const filterValue = rawValue != null ? rawValue : clickedDisplayValue;

	// Fetch raw detail rows for a single configured measure - looping over every dataConfig
	// entry (not just the first) is what lets multi-measure widgets show detail rows for ALL
	// their measures and each measure's own configured filters, not only the first one.
	const fetchMeasureDetail = async (measureConfig) => {
		const filters = { ...(measureConfig.filters || {}) };
		if (xSeriesDimension && filterValue) {
			filters[xSeriesDimension] = Array.isArray(filterValue) ? filterValue : [filterValue];
		}

		try {
			if (measureConfig.indicatorId) {
				const indicatorConfig = {
					indicatorId: measureConfig.indicatorId,
					seriesOn: [], // Empty seriesOn to get raw data without grouping
					filters,
					limit,
					offset,
					getAllColumns: true // Signal to backend to return all columns
				};
				const response = await axios.post('/indicator/getTable', indicatorConfig);
				const data = response.data?.response || [];
				return { data, totalCount: response.data?.totalCount || data.length };
			}
			if (measureConfig.queryId) {
				const queryConfig = {
					...(isExternal
						? { thirdParty: { exploreId: measureConfig.queryId }, organizationId: parseInt(organizationId, 10) || 0 }
						: { queryId: measureConfig.queryId }
					),
					seriesOn: [], // Empty seriesOn to get raw data
					filters,
					limit,
					offset,
					getAllColumns: true, // Signal to backend to return all columns
					aggregation: 'none' // No aggregation for drill-to-detail
				};
				const response = await axios.post('/api/facts/filter', queryConfig);
				const data = response.data?.response || [];
				return { data, totalCount: response.data?.totalCount || data.length };
			}
			return { data: [], totalCount: 0 };
		} catch (error) {
			console.error('Error fetching drill-to-detail data for measure:', error);
			return { data: [], totalCount: 0 };
		}
	};

	try {
		const perMeasureResults = await Promise.all(dataConfig.map(fetchMeasureDetail));
		const data = perMeasureResults.flatMap(r => r.data);
		const totalCount = perMeasureResults.reduce((sum, r) => sum + (r.totalCount || 0), 0);
		const columns = extractColumnsFromData(data);
		return { data, columns, totalCount };
	} catch (error) {
		console.error('Error fetching drill-to-detail data:', error);
		return { data: [], columns: [], totalCount: 0 };
	}
};

/**
 * Extracts all unique column names from an array of data objects
 * Orders columns with common ones first (like IDs, names) then alphabetically
 *
 * @param {Array} data - Array of data objects
 * @returns {Array} - Array of column definitions with name and type
 */
const extractColumnsFromData = (data) => {
	if (!Array.isArray(data) || data.length === 0) {
		return [];
	}

	const columnSet = new Set();
	const columnTypes = {};

	// Collect all unique column names and infer types
	data.forEach(row => {
		if (row && typeof row === 'object') {
			Object.keys(row).forEach(key => {
				// Skip internal/meta fields
				if (key.startsWith('_')) return;
				
				columnSet.add(key);
				
				// Infer type from the first non-null value
				if (!columnTypes[key] && row[key] !== null && row[key] !== undefined) {
					const value = row[key];
					if (typeof value === 'number') {
						columnTypes[key] = 'number';
					} else if (typeof value === 'boolean') {
						columnTypes[key] = 'boolean';
					} else if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)) && value.includes('-'))) {
						columnTypes[key] = 'date';
					} else {
						columnTypes[key] = 'string';
					}
				}
			});
		}
	});

	// Priority columns that should appear first
	const priorityColumns = ['id', 'name', 'title', 'date', 'created_at', 'updated_at'];
	
	const columns = Array.from(columnSet).map(name => ({
		name,
		type: columnTypes[name] || 'string',
		accessor: name,
		Header: formatColumnHeader(name)
	}));

	// Sort: priority columns first, then alphabetically
	columns.sort((a, b) => {
		const aIdx = priorityColumns.findIndex(p => a.name.toLowerCase().includes(p));
		const bIdx = priorityColumns.findIndex(p => b.name.toLowerCase().includes(p));
		
		if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
		if (aIdx !== -1) return -1;
		if (bIdx !== -1) return 1;
		return a.name.localeCompare(b.name);
	});

	return columns;
};

/**
 * Formats a column name into a human-readable header
 * e.g., "first_name" -> "First Name", "userId" -> "User Id"
 *
 * @param {string} name - Column name
 * @returns {string} - Formatted header
 */
const formatColumnHeader = (name) => {
	if (!name) return '';
	
	return name
		// Convert snake_case to spaces
		.replace(/_/g, ' ')
		// Convert camelCase to spaces
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		// Capitalize first letter of each word
		.replace(/\b\w/g, char => char.toUpperCase());
};

export default {
	fetchDrillByDimensions,
	fetchDrillByData,
	fetchDrillToDetailData
};
